/**
 * Утилиты обработки ошибок HTTP/приложения (foundation-версия EyeHub).
 *
 * Компактный self-contained модуль без импорта i18n (во избежание циклических
 * зависимостей: apiClient → errorHandler, а axiosBaseQuery → apiClient + i18n).
 * Вызывающий код передаёт уже локализованный default-текст, если нужен.
 *
 * Экспортирует поверхность, на которую опирается шелл:
 *   parseHttpError, extractErrorMessage, handleHttpError, extractFieldErrors,
 *   extractRequestId, extractErrorTimestamp, isCriticalError, isAuthError,
 *   initializeErrorHandlers.
 *
 * @module utils/errorHandler
 */

import { logger } from './logger';

const DEFAULT_MESSAGE = 'Произошла ошибка. Попробуйте позже.';

/**
 * Достаёт человекочитаемое сообщение из axios/fetch-ошибки.
 * Разбирает unified-envelope ({data:null, error:{message,code}}), DRF-detail,
 * массивы, строки и стандартный Error.
 *
 * @param {*} error
 * @returns {string}
 */
export function parseHttpError(error) {
  if (!error) return DEFAULT_MESSAGE;
  if (typeof error === 'string') return error;

  // Тело ответа (axios: error.response.data, либо переданный объект напрямую)
  const data = error.response?.data ?? error.data ?? error;

  if (data && typeof data === 'object') {
    // Unified envelope
    if (data.error && typeof data.error === 'object') {
      if (data.error.message) return data.error.message;
    }
    if (typeof data.error === 'string') return data.error;
    // DRF
    if (data.detail) return Array.isArray(data.detail) ? data.detail.join(' ') : String(data.detail);
    if (data.message) return String(data.message);
    if (Array.isArray(data.non_field_errors)) return data.non_field_errors.join(' ');
  }

  if (error.message) return error.message;
  return DEFAULT_MESSAGE;
}

/**
 * Извлекает сообщение об ошибке с fallback-значением.
 * Основной хелпер для slice/hooks (RTK Query rejected payload).
 *
 * @param {*} error
 * @param {string|null} defaultMessage
 * @returns {string}
 */
export function extractErrorMessage(error, defaultMessage = null) {
  const fallback = defaultMessage || DEFAULT_MESSAGE;
  if (!error) return fallback;
  if (typeof error === 'string') return error;

  // RTK Query error shape: { status, data }
  if (error.data !== undefined) {
    const parsed = parseHttpError({ data: error.data });
    if (parsed && parsed !== DEFAULT_MESSAGE) return parsed;
  }

  const parsed = parseHttpError(error);
  return parsed || fallback;
}

/**
 * Альтернатива extractErrorMessage для auth-потоков (login/logout).
 * @param {*} error
 * @returns {string}
 */
export function handleHttpError(error) {
  return extractErrorMessage(error, DEFAULT_MESSAGE);
}

/**
 * Извлекает пофилдовые ошибки валидации (для форм Formik).
 * @param {*} error
 * @returns {Object<string,string>} { field: message }
 */
export function extractFieldErrors(error) {
  const data = error?.response?.data ?? error?.data ?? error;
  const result = {};
  if (!data || typeof data !== 'object') return result;

  const source = data.error?.fields || data.fields || data;
  Object.entries(source).forEach(([key, value]) => {
    if (key === 'message' || key === 'code' || key === 'status') return;
    result[key] = Array.isArray(value) ? value.join(' ') : String(value);
  });
  return result;
}

/**
 * Достаёт correlation-id запроса (`RequestIDMiddleware` бэкенда).
 *
 * Ищет по порядку: unified-envelope (`error.request_id` / `requestId`), тело
 * ответа и заголовки axios (`x-request-id`, `x-correlation-id`). Нужен
 * компоненту ErrorDisplay: без id поддержка не найдёт запрос в логах.
 *
 * @param {*} error
 * @returns {string|null}
 */
export function extractRequestId(error) {
  if (!error || typeof error !== 'object') return null;

  const data = error.response?.data ?? error.data ?? error;
  const envelope = data?.error && typeof data.error === 'object' ? data.error : data;

  const fromBody =
    envelope?.request_id ??
    envelope?.requestId ??
    data?.request_id ??
    data?.requestId ??
    error.request_id ??
    error.requestId;
  if (fromBody) return String(fromBody);

  const headers = error.response?.headers;
  if (headers) {
    const fromHeader =
      (typeof headers.get === 'function'
        ? headers.get('x-request-id') || headers.get('x-correlation-id')
        : headers['x-request-id'] || headers['x-correlation-id']) || null;
    if (fromHeader) return String(fromHeader);
  }

  return null;
}

/**
 * Достаёт отметку времени ошибки из unified-envelope (ISO-строка).
 *
 * @param {*} error
 * @returns {string|null}
 */
export function extractErrorTimestamp(error) {
  if (!error || typeof error !== 'object') return null;

  const data = error.response?.data ?? error.data ?? error;
  const envelope = data?.error && typeof data.error === 'object' ? data.error : data;

  const value =
    envelope?.timestamp ??
    envelope?.time ??
    data?.timestamp ??
    error.timestamp ??
    null;
  return value ? String(value) : null;
}

/**
 * Является ли ошибка критической (5xx / сеть).
 * @param {*} error
 * @returns {boolean}
 */
export function isCriticalError(error) {
  const status = error?.response?.status ?? error?.status;
  if (status && status >= 500) return true;
  // Сетевые ошибки axios — нет response
  if (error && error.request && !error.response) return true;
  return false;
}

/**
 * Является ли ошибка ошибкой аутентификации (401/403).
 * @param {*} error
 * @returns {boolean}
 */
export function isAuthError(error) {
  const status = error?.response?.status ?? error?.status;
  return status === 401 || status === 403;
}

/**
 * Регистрирует глобальные обработчики необработанных ошибок/промисов.
 * Вызывается один раз в index.js до рендера.
 */
export function initializeErrorHandlers() {
  if (typeof window === 'undefined') return;

  window.addEventListener('unhandledrejection', (event) => {
    const message = parseHttpError(event.reason);
    logger.error('Unhandled promise rejection:', message, event.reason);
  });

  window.addEventListener('error', (event) => {
    logger.error('Global error:', event.message, event.error);
  });
}

const errorHandlerUtils = {
  parseHttpError,
  extractErrorMessage,
  handleHttpError,
  extractFieldErrors,
  extractRequestId,
  extractErrorTimestamp,
  isCriticalError,
  isAuthError,
  initializeErrorHandlers,
};

export default errorHandlerUtils;
